import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import PageHeader from "../../components/PageHeader";
import StandardTable from "../../ui/components/StandardTable";
import StandardTableFilterDropdown from "../../ui/components/StandardTableFilterDropdown";
import StatusBadge from "../../ui/components/StatusBadge";
import Stepper from "../../ui/components/Stepper";
import TableActionsColumn from "../../ui/components/TableActionsColumn";
import Pagination from "../../components/utils/Pagination";
import EmptyState from "../../components/EmptyState";
import {
  PeriodFilterModal,
  SingleSelectFilterModal,
  TableFilterBar,
  TableFilterButton,
} from "../../ui/components/filters";
import { getStatusOptions } from "../characterization-2/characterizationList/StatusFilterModal";

import pinSvg from "../../static/assets/Pin.svg";
import clientSvg from "../../static/assets/Client.svg";
import assignmentSvg from "../../static/assets/Assignment.svg";
import weightSvg from "../../static/assets/Weight.svg";
import trashSvg from "../../static/assets/Trash.svg";
import exporterSvg from "../../static/assets/Exporter.svg";
import settingsSvg from "../../static/assets/Settings.svg";
import historySvg from "../../static/assets/History.svg";
import folderSvg from "../../static/assets/Empty-folder.svg";
import plasticSvg from "../../static/assets/Plastic-bin.svg";
import garbageBagSvg from "../../static/assets/Garbage-bag.svg";

import settingsSitesSvg from "../../static/assets/icons/settings/Sites.svg";
import settingsClientsSvg from "../../static/assets/icons/settings/Clients.svg";
import settingsWasteSvg from "../../static/assets/icons/settings/Waste.svg";
import settingsContainersSvg from "../../static/assets/icons/settings/Containers.svg";
import settingsCamerasSvg from "../../static/assets/icons/settings/Cameras.svg";
import settingsContactsSvg from "../../static/assets/icons/settings/Contacts.svg";
import settingsConstantsSvg from "../../static/assets/icons/settings/Constants.svg";

const SVG_LOGO_ITEMS = [
  { name: "Sites / Localisation", file: "Pin.svg", src: pinSvg },
  { name: "Client / Origine des déchets", file: "Client.svg", src: clientSvg },
  { name: "Flux / Assignment", file: "Assignment.svg", src: assignmentSvg },
  { name: "Poids / Tonnage", file: "Weight.svg", src: weightSvg },
  { name: "Déchets / Poubelle", file: "Trash.svg", src: trashSvg },
  { name: "Export", file: "Exporter.svg", src: exporterSvg },
  { name: "Paramètres", file: "Settings.svg", src: settingsSvg },
  { name: "Historique", file: "History.svg", src: historySvg },
  { name: "État vide / Dossier", file: "Empty-folder.svg", src: folderSvg },
  { name: "Plastique", file: "Plastic-bin.svg", src: plasticSvg },
  { name: "Sac déchets", file: "Garbage-bag.svg", src: garbageBagSvg },
  {
    name: "Settings — Sites",
    file: "icons/settings/Sites.svg",
    src: settingsSitesSvg,
  },
  {
    name: "Settings — Origine des déchets",
    file: "icons/settings/Clients.svg",
    src: settingsClientsSvg,
  },
  {
    name: "Settings — Flux des déchets",
    file: "icons/settings/Waste.svg",
    src: settingsWasteSvg,
  },
  {
    name: "Settings — Zones d’analyse",
    file: "icons/settings/Containers.svg",
    src: settingsContainersSvg,
  },
  {
    name: "Settings — Capteurs",
    file: "icons/settings/Cameras.svg",
    src: settingsCamerasSvg,
  },
  {
    name: "Settings — Contacts collecte",
    file: "icons/settings/Contacts.svg",
    src: settingsContactsSvg,
  },
  {
    name: "Settings — Paramètres généraux",
    file: "icons/settings/Constants.svg",
    src: settingsConstantsSvg,
  },
];

const PALETTE_COLORS = [
  { name: "Cyan", var: "--akanthas-cyan", hex: "#00A7A6" },
  { name: "Bleu", var: "--akanthas-blue", hex: "#0F265C" },
  { name: "Rouge", var: "--akanthas-red", hex: "#E5193C" },
  { name: "Jaune", var: "--akanthas-yellow", hex: "#f8AA00" },
  { name: "Gris", var: "--grey", hex: "#828899" },
  { name: "Blush", var: "--blush", hex: "#E81D7A" },
  { name: "Light grey", var: "--light-grey", hex: "#F6F6F6" },
  { name: "Gainsboro", var: "--gainsboro", hex: "#DCDCDC" },
  { name: "Status green", var: "--status-green", hex: "#7ED958" },
  { name: "Status orange", var: "--status-orange", hex: "#FFBD59" },
  { name: "Status red", var: "--status-red", hex: "#FF1617" },
  { name: "Status red danger", var: "--status-red-danger", hex: "#AC0001" },
];

const FONT_AWESOME_ICONS = [
  { name: "Recherche", class: "fa-search" },
  { name: "Chevron bas", class: "fa-chevron-down" },
  { name: "Chevron haut", class: "fa-chevron-up" },
  { name: "Actions (trois points)", class: "fa-ellipsis-v" },
  { name: "Valider", class: "fa-check" },
  { name: "En cours", class: "fa-history" },
  { name: "Alerte", class: "fa-exclamation-triangle" },
  { name: "Aide", class: "fa-question-circle" },
  { name: "Modifier", class: "fa-pen" },
  { name: "Tri", class: "fa-sort" },
  { name: "Tri asc", class: "fa-sort-up" },
  { name: "Tri desc", class: "fa-sort-down" },
  { name: "Fermer", class: "fa-times" },
  { name: "Ajouter", class: "fa-plus" },
  { name: "Liste", class: "fa-list" },
  { name: "Graphique", class: "fa-chart-bar" },
  { name: "Notifications", class: "fa-bullhorn" },
  { name: "Paramètres", class: "fa-cog" },
  { name: "UI kit", class: "fa-palette" },
  { name: "E-mail", class: "fa-envelope" },
  { name: "Dossier", class: "fa-folder" },
];

const SAMPLE_ROWS_3 = [
  { id: 1, name: "Lorem ipsum dolor sit amet", status: "Validated" },
  { id: 2, name: "Consectetur adipiscing elit", status: "In progress" },
  { id: 3, name: "Sed do eiusmod tempor", status: "Error" },
];

const LONG_ORIGIN_TEXT =
  "Client Lorem Ipsum Dolor Sit Amet — 123 rue de la République, immeuble des Longs Textes, bâtiment B, étage 3, code accès 4567, zone industrielle nord-est, 75000 Paris, France";

const SAMPLE_ROWS_5 = [
  {
    id: 1,
    name: "Lorem ipsum dolor",
    status: "Validated",
    origin: LONG_ORIGIN_TEXT,
    lastModif: "28/01/2025",
  },
  {
    id: 2,
    name: "Consectetur adipiscing",
    status: "In progress",
    origin: "Client B — 456 avenue Test",
    lastModif: "27/01/2025",
  },
  {
    id: 3,
    name: "Sed do eiusmod",
    status: "Error",
    origin: LONG_ORIGIN_TEXT,
    lastModif: "26/01/2025",
  },
];

const LOREM_FILTER_OPTIONS = [
  { value: "ipsum", label: "Ipsum" },
  { value: "dolor", label: "Dolor" },
  { value: "sit", label: "Sit amet" },
];

const TableTopBar = ({ placeholder }) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [loremFilter, setLoremFilter] = useState("");

  return (
    <section className="table-standard-top-bar">
      <div className="table-standard-top-bar-inner">
        <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
          <div className="search-input-wrapper">
            <i className="fas fa-search search-input-icon" aria-hidden />
            <input
              type="text"
              className="search-input"
              value={searchValue}
              placeholder={placeholder ?? t("Search")}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
          <StandardTableFilterDropdown
            label="Lorem"
            value={loremFilter}
            options={LOREM_FILTER_OPTIONS}
            allLabel="Lorem — All"
            onChange={setLoremFilter}
          />
        </div>
      </div>
    </section>
  );
};

const TableRowEditDeleteDemo = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const actionsButtonRef = useRef(null);

  const updateMenuPosition = () => {
    if (actionsButtonRef.current) {
      const rect = actionsButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - 120,
      });
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        actionsButtonRef.current &&
        !actionsButtonRef.current.contains(e.target) &&
        !e.target.closest(".table-standard-row-actions-menu-portal")
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="table-standard-row">
      <div className="table-standard-row-cell">{t("Name")} 1</div>
      <div className="table-standard-row-cell">{t("Role")}</div>
      <div className="table-standard-row-cell table-standard-row-actions">
        <button
          ref={actionsButtonRef}
          type="button"
          className="table-standard-row-actions-dots"
          onClick={(e) => {
            e.stopPropagation();
            if (!isOpen && actionsButtonRef.current) {
              const rect = actionsButtonRef.current.getBoundingClientRect();
              setMenuPosition({
                top: rect.bottom + 4,
                left: rect.right - 120,
              });
            }
            setIsOpen(!isOpen);
          }}
          aria-label={t("Actions")}
          aria-expanded={isOpen}
        >
          <i
            className={isOpen ? "fas fa-times" : "fas fa-ellipsis-v"}
            aria-hidden
          />
        </button>
        {isOpen &&
          createPortal(
            <div
              className="table-standard-row-actions-menu-portal action-choice display-action"
              style={{
                position: "fixed",
                top: menuPosition.top,
                left: menuPosition.left,
                zIndex: 9999,
                width: "auto",
                minWidth: 120,
                maxWidth: 200,
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 12,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                overflow: "hidden",
              }}
            >
              <button type="button" className="action">
                {t("Edit")}
              </button>
              <button type="button" className="action">
                {t("Delete")}
              </button>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

const UiKitFiltersDemo = () => {
  const { t } = useTranslation();
  const [periodLabel, setPeriodLabel] = useState(t("Last 6 months"));
  const [periodFilter, setPeriodFilter] = useState(6);
  const [periodStart, setPeriodStart] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const statusOptions = getStatusOptions(t);
  const statusLabel =
    statusOptions.find((option) => option.value === statusFilter)?.label ??
    t("All");

  return (
    <section className="mb-8">
      <h2 className="char-section-header-title mb-4">{t("Filters")}</h2>
      <p className="char-text-14 char-text-secondary mb-6">
        {t(
          "Shared filter components used on list pages (Period, Status, search bar). Reference: characterization-2 list.",
        )}
      </p>

      <Card className="table mb-6">
        <TableFilterBar
          searchValue={searchValue}
          searchPlaceholder={t("Search for a client")}
          onSearchChange={setSearchValue}
          filters={
            <>
              <TableFilterButton
                label={t("Period")}
                value={periodLabel}
                onClick={() => setIsPeriodModalOpen(true)}
              />
              <TableFilterButton
                label={t("Status")}
                value={statusLabel}
                onClick={() => setIsStatusModalOpen(true)}
              />
            </>
          }
        />
      </Card>

      <div className="grid grid-cols-1 min-1000:grid-cols-2 gap-4">
        <div className="border border-gray-200 rounded-lg bg-white p-4">
          <h3 className="text-lg font-semibold text-[#222222] mb-3">
            TableFilterButton
          </h3>
          <div className="flex flex-wrap gap-2">
            <TableFilterButton
              label={t("Period")}
              value={t("Last 6 months")}
              onClick={() => {}}
            />
            <TableFilterButton
              label={t("Status")}
              value={t("All")}
              onClick={() => {}}
            />
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg bg-white p-4">
          <h3 className="text-lg font-semibold text-[#222222] mb-3">
            SingleSelectFilterModal
          </h3>
          <button
            type="button"
            className="char-button char-button-primary"
            onClick={() => setIsStatusModalOpen(true)}
          >
            {t("Open status filter")}
          </button>
        </div>
      </div>

      <PeriodFilterModal
        isOpen={isPeriodModalOpen}
        onClose={() => setIsPeriodModalOpen(false)}
        currentPeriodFilter={periodFilter}
        currentPeriodStart={periodStart}
        currentPeriodEnd={periodEnd}
        onApply={(months, label, startDateIso, endDateIso) => {
          setPeriodFilter(months);
          setPeriodLabel(label || t("Last 6 months"));
          setPeriodStart(startDateIso);
          setPeriodEnd(endDateIso);
        }}
      />

      <SingleSelectFilterModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        title={t("Status")}
        options={statusOptions}
        currentValue={statusFilter}
        onApply={setStatusFilter}
      />
    </section>
  );
};

const UiKit = () => {
  const { t } = useTranslation();
  const [activeUiKitTab, setActiveUiKitTab] = useState("tableaux");
  const [activeHeaderDetailTab, setActiveHeaderDetailTab] = useState("reports");
  const [activeMonitoringDetailTab, setActiveMonitoringDetailTab] =
    useState("activity");
  const [stepperDemoIndex, setStepperDemoIndex] = useState(1);
  const [showConstructionGrid, setShowConstructionGrid] = useState(true);
  const [showPromptText, setShowPromptText] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [resultPage, setResultPage] = useState(0);
  const [emptyStateSize, setEmptyStateSize] = useState({ width: 0, height: 0 });
  const [headersSectionWidth, setHeadersSectionWidth] = useState(0);
  const emptyStateRef = useRef(null);
  const headersSectionRef = useRef(null);
  const exportIconGray = (
    <img
      src={exporterSvg}
      alt=""
      className="w-4 h-4"
      style={{ filter: "brightness(0) invert(0.45)" }}
    />
  );
  const iconGray = "text-[#6B7280]";

  const totalEntryDemo = 25;

  useEffect(() => {
    const el = emptyStateRef.current;
    if (!el) return;
    const update = () =>
      setEmptyStateSize({
        width: Math.round(el.offsetWidth),
        height: Math.round(el.offsetHeight),
      });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (activeUiKitTab !== "headers") return;
    const el = headersSectionRef.current;
    if (!el) return;
    const update = () => setHeadersSectionWidth(Math.round(el.offsetWidth));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeUiKitTab]);

  return (
    <div className="settings-page-wrapper">
      <PageHeader
        title={t("UI kit")}
        tabs={[
          { id: "tableaux", label: t("Tables") },
          { id: "filtres", label: t("Filters") },
          { id: "headers", label: t("Headers") },
          { id: "steppers", label: t("Steppers") },
          { id: "couleur", label: t("Palette de couleur") },
          { id: "logo", label: t("Logos") },
        ]}
        activeTab={activeUiKitTab}
        onTabChange={setActiveUiKitTab}
      />
      <div className="settings-content-wrapper">
        {activeUiKitTab === "tableaux" && (
          <>
            <section className="mb-6">
              <h2 className="char-section-header-title mb-4">{t("Tables")}</h2>

              <div
                className="border-l-4 border-amber-500 bg-amber-50 p-4 rounded-r mb-4"
                role="note"
                aria-label={t("Example prompt for Cursor")}
              >
                <button
                  type="button"
                  onClick={() => setShowPromptText((prev) => !prev)}
                  className="flex items-center justify-between w-full text-left focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 rounded"
                  aria-expanded={showPromptText}
                >
                  <span className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
                    {t("Important")} — {t("Example prompt for Cursor")}
                  </span>
                  <i
                    className={`fas fa-chevron-${showPromptText ? "up" : "down"} text-amber-800 text-xs shrink-0 ml-2`}
                    aria-hidden
                  />
                </button>
                {showPromptText && (
                  <p className="text-sm text-gray-800 font-mono whitespace-pre-wrap break-words mt-2">
                    {`Je suis en train de construire un nouveau tableau. Je veux que tu t'inspires de l'UI kit (page /ui-kit, composants dans ui/components).

Utilise le composant StandardTable :
- Avec bouton/menu en fin de ligne : fluidColCount={N colonnes DONNEES} + fixedColWidth={70|100|150}
- Sans bouton en fin de ligne : fluidColCount={N colonnes TOTAL} + fixedColWidth={0}
Passe la barre du haut (recherche + filtre) en prop topBar pour qu'elle suive la même largeur que le tableau.

Respecte strictement .cursor/rules/frontend-tables-standard.mdc et ui/ui-guidelines.md :
- Colonnes fluides : 300px min, 450px référence ; référence = conteneur parent.
- 3 zones : P < min → scroll ; min ≤ P ≤ ref → 100% ; P > ref → plafond ref, centré (clamp CSS).
- Grille CSS : minmax(0, 1fr) ou minmax(0, Xfr) — jamais minmax(Npx, …).
- Pour les cellules avec texte long : span avec classe table-standard-row-cell-truncate.`}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-3 mb-4">
                <p className="char-text-14 char-text-secondary m-0">
                  {t(
                    "The dotted lines below are shown only in the UI Kit to illustrate the table structure. They do not appear on other pages.",
                  )}
                </p>
                <p className="char-text-14 char-text-secondary m-0">
                  {t("Table standard truncation guideline")}
                </p>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none w-fit">
                  <input
                    type="checkbox"
                    checked={showConstructionGrid}
                    onChange={(e) => setShowConstructionGrid(e.target.checked)}
                    className="sr-only peer"
                  />
                  <span className="relative inline-block w-9 h-5 shrink-0 rounded-full bg-gray-200 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:shadow after:transition-transform after:duration-200 after:content-[''] peer-checked:bg-[#00A7A6] peer-checked:after:translate-x-4" />
                  <span className="char-text-14 char-text-primary font-medium">
                    {t("Show construction grid")}
                  </span>
                </label>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[#222222] mb-3">
                  {t("Table — 3 columns")} (2 {t("fluid")} + 1 {t("fixed")}{" "}
                  150px)
                </h3>
                <Card className="table">
                  <StandardTable
                    fluidColCount={2}
                    columnAlignments={["left", "left", "right"]}
                    showConstructionGrid={showConstructionGrid}
                    topBar={<TableTopBar placeholder={t("Search")} />}
                    header={
                      <div className="table-standard-header">
                        <div className="table-standard-header-cell">
                          {t("Name")}
                        </div>
                        <div className="table-standard-header-cell">
                          {t("Status")}
                        </div>
                        <div className="table-standard-header-cell" />
                      </div>
                    }
                    bottom={
                      <div className="table-standard-bottom table-pagination-bottom">
                        <Pagination
                          totalEntry={totalEntryDemo}
                          recordPerPage={recordPerPage}
                          setRecordPerPage={setRecordPerPage}
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          resultPage={resultPage}
                          setResultPage={setResultPage}
                        />
                      </div>
                    }
                  >
                    {SAMPLE_ROWS_3.map((row) => (
                      <div key={row.id} className="table-standard-row">
                        <div className="table-standard-row-cell">
                          {row.name}
                        </div>
                        <div className="table-standard-row-cell">
                          <StatusBadge status={row.status} />
                        </div>
                        <TableActionsColumn
                          onExportPdf={() => {}}
                          onExportCsv={() => {}}
                          onSendEmail={() => {}}
                        />
                      </div>
                    ))}
                  </StandardTable>
                </Card>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-[#222222] mb-3">
                  {t("Table — 5 columns")} (4 {t("fluid")} + 1 {t("fixed")}{" "}
                  100px, {t("Actions")})
                </h3>
                <Card className="table">
                  <StandardTable
                    fluidColCount={4}
                    fixedColWidth={100}
                    columnAlignments={[
                      "left",
                      "left",
                      "left",
                      "right",
                      "right",
                    ]}
                    showConstructionGrid={showConstructionGrid}
                    topBar={<TableTopBar placeholder={t("Search")} />}
                    header={
                      <div className="table-standard-header">
                        <div className="table-standard-header-cell">
                          {t("Name")}
                        </div>
                        <div className="table-standard-header-cell">
                          {t("Status")}
                        </div>
                        <div className="table-standard-header-cell">
                          {t("Waste origin")}
                        </div>
                        <div className="table-standard-header-cell">
                          {t("Last modification")}
                        </div>
                        <div className="table-standard-header-cell" />
                      </div>
                    }
                    bottom={
                      <div className="table-standard-bottom table-pagination-bottom">
                        <Pagination
                          totalEntry={totalEntryDemo}
                          recordPerPage={recordPerPage}
                          setRecordPerPage={setRecordPerPage}
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          resultPage={resultPage}
                          setResultPage={setResultPage}
                        />
                      </div>
                    }
                  >
                    {SAMPLE_ROWS_5.map((row) => (
                      <div key={row.id} className="table-standard-row">
                        <div className="table-standard-row-cell">
                          {row.name}
                        </div>
                        <div className="table-standard-row-cell">
                          <StatusBadge status={row.status} />
                        </div>
                        <div className="table-standard-row-cell">
                          <span className="table-standard-row-cell-truncate">
                            {row.origin}
                          </span>
                        </div>
                        <div className="table-standard-row-cell">
                          {row.lastModif}
                        </div>
                        <div className="table-standard-row-cell table-standard-row-actions">
                          <button
                            type="button"
                            className="table-standard-row-actions-dots"
                            aria-label={t("Actions")}
                          >
                            <i className="fas fa-ellipsis-v" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </StandardTable>
                </Card>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[#222222] mb-3">
                  {t("No results match your search")}
                </h3>
                <Card className="table">
                  <StandardTable
                    fluidColCount={2}
                    showConstructionGrid={showConstructionGrid}
                    showConstructionWidthLabels={false}
                    topBar={<TableTopBar placeholder={t("Search")} />}
                    header={
                      <div className="table-standard-header">
                        <div className="table-standard-header-cell">
                          {t("Name")}
                        </div>
                        <div className="table-standard-header-cell">
                          {t("Status")}
                        </div>
                        <div className="table-standard-header-cell" />
                      </div>
                    }
                    bottom={
                      <div className="table-standard-bottom table-pagination-bottom">
                        <Pagination
                          totalEntry={totalEntryDemo}
                          recordPerPage={recordPerPage}
                          setRecordPerPage={setRecordPerPage}
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          resultPage={resultPage}
                          setResultPage={setResultPage}
                        />
                      </div>
                    }
                  >
                    <div className="table-standard-row table-standard-row--empty">
                      <div
                        className="table-standard-row-cell"
                        style={{ gridColumn: "1 / -1" }}
                      >
                        {t("No results match your search")}
                      </div>
                    </div>
                  </StandardTable>
                </Card>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[#222222] mb-3">
                  {t("Edit")} / {t("Delete")} {t("Actions")}
                </h3>
                <p className="char-text-14 char-text-secondary mb-3">
                  {t(
                    "Click the three dots to open the menu. Use table-standard-row-actions-dots + action-choice with .display-action when open.",
                  )}
                </p>
                <Card className="table">
                  <StandardTable
                    fluidColCount={2}
                    columnAlignments={["left", "left", "right"]}
                    showConstructionGrid={showConstructionGrid}
                    showConstructionWidthLabels={false}
                    header={
                      <div className="table-standard-header">
                        <div className="table-standard-header-cell">
                          {t("Name")}
                        </div>
                        <div className="table-standard-header-cell">
                          {t("Role")}
                        </div>
                        <div className="table-standard-header-cell" />
                      </div>
                    }
                  >
                    <TableRowEditDeleteDemo />
                  </StandardTable>
                </Card>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="char-section-header-title mb-4">
                {t("Empty state")}
              </h2>
              <p className="char-text-14 char-text-secondary mb-4">
                {t(
                  "Bloc type « état vide » (composant EmptyState). Largeur et hauteur de référence.",
                )}
              </p>
              <div className="w-full flex flex-col items-center">
                {emptyStateSize.width > 0 && (
                  <div className="text-center text-[11px] text-gray-400 mb-1">
                    {t("Largeur totale")} : {emptyStateSize.width}px
                  </div>
                )}
                <div
                  ref={emptyStateRef}
                  className="relative rounded-lg overflow-hidden w-full max-w-[1250px] h-[430px] mx-auto"
                >
                  <EmptyState
                    title={t("Aucun élément")}
                    description={t(
                      "Ajoutez un premier élément pour commencer.",
                    )}
                    buttonText={t("Ajouter")}
                    onButtonClick={() => {}}
                  />
                  {emptyStateSize.width > 0 && emptyStateSize.height > 0 && (
                    <>
                      <div
                        className="absolute top-2 bottom-2 left-12 flex flex-col items-stretch pointer-events-none z-10"
                        aria-hidden
                      >
                        <span className="w-0 h-0 border-l-[3px] border-l-transparent border-r-[3px] border-r-transparent border-b-[4px] border-b-gray-400 shrink-0 self-center" />
                        <span className="flex-1 border-l-2 border-l-gray-400 border-dashed w-px self-center min-h-0" />
                        <span className="w-0 h-0 border-l-[3px] border-l-transparent border-r-[3px] border-r-transparent border-t-[4px] border-t-gray-400 shrink-0 self-center" />
                      </div>
                      <div className="absolute top-1/2 -translate-y-1/2 left-20 text-[11px] text-gray-400 whitespace-nowrap z-10">
                        {t("Hauteur")} : {emptyStateSize.height}px
                      </div>
                    </>
                  )}
                </div>
              </div>
            </section>
          </>
        )}

        {activeUiKitTab === "headers" && (
          <section className="mb-8">
            <h2 className="char-section-header-title mb-4">{t("Headers")}</h2>
            <p className="char-text-14 char-text-secondary mb-6">
              {t("All page and table headers used in the application.")}
            </p>
            <div ref={headersSectionRef} className="w-full">
              {headersSectionWidth > 0 && (
                <div className="table-standard-construction-total">
                  Largeur : {headersSectionWidth}px
                </div>
              )}
              <div className="flex flex-col gap-12">
                <div>
                  <h3 className="text-lg font-semibold text-[#222222] mb-3">
                    Caractérisation — Zones
                  </h3>
                  <PageHeader title={t("Characterizations")} tabs={[]} />
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#222222] mb-3">
                    Caractérisation — Liste des caractérisations
                  </h3>
                  <PageHeader
                    titleContent={
                      <div className="flex items-center gap-3 flex-wrap">
                        <span>Lorem ipsum zone</span>
                        <span
                          className="inline-flex items-center justify-center rounded-full text-sm font-bold text-[#2563EB]"
                          style={{
                            padding: "4px 12px",
                            height: "30px",
                            background: "#E6ECFD",
                            borderRadius: "1000px",
                          }}
                        >
                          Lorem
                        </span>
                      </div>
                    }
                    titleContentMain={<span>Lorem ipsum zone</span>}
                    titleContentBadges={
                      <span
                        className="inline-flex items-center justify-center rounded-full text-sm font-bold text-[#2563EB]"
                        style={{
                          padding: "4px 12px",
                          height: "30px",
                          background: "#E6ECFD",
                          borderRadius: "1000px",
                        }}
                      >
                        Lorem
                      </span>
                    }
                    showBackButton
                    backRoute="#"
                    backLabel={t("Back to Zones")}
                    shareButton={
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold text-[#222222] border border-[#d3d3d3] bg-white hover:bg-[#f8f8f8] transition-colors"
                      >
                        <img src={settingsSvg} alt="" className="w-4 h-4" />
                        {t("Zone settings")}
                      </button>
                    }
                    actionsMenuItems={[
                      {
                        label: t("Zone settings"),
                        icon: (
                          <img src={settingsSvg} alt="" className="w-4 h-4" />
                        ),
                        onClick: () => {},
                      },
                    ]}
                  />
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#222222] mb-3">
                    Caractérisation — Détail
                  </h3>
                  <PageHeader
                    showBackButton
                    backRoute="#"
                    backLabel={`${t("Back to")} Lorem ipsum center`}
                    titleContent={
                      <div className="flex items-center gap-3 flex-wrap">
                        <span>Lorem ipsum 15/01/2025</span>
                        <span className="char-badge char-badge-outline">
                          Lorem ipsum client
                        </span>
                        <StatusBadge status="Validated" />
                      </div>
                    }
                    titleContentMain={<span>Lorem ipsum 15/01/2025</span>}
                    titleContentBadges={
                      <>
                        <span className="char-badge char-badge-outline">
                          Lorem ipsum client
                        </span>
                        <StatusBadge status="Validated" />
                      </>
                    }
                    actions={
                      <div className="flex flex-row items-center gap-2">
                        <button type="button" className="char-button">
                          <i className="fas fa-history text-sm" />
                          <span>{t("All client characterizations")}</span>
                        </button>
                        <div className="relative">
                          <button
                            type="button"
                            className="char-button char-button-primary"
                          >
                            <img
                              src={exporterSvg}
                              alt="export"
                              className="char-button-icon"
                              style={{ filter: "brightness(0) invert(1)" }}
                            />
                            <span>{t("Export")}</span>
                            <i className="fas fa-chevron-down text-xs" />
                          </button>
                        </div>
                      </div>
                    }
                    tabs={[
                      { id: "reports", label: t("Reports") },
                      { id: "files", label: t("Files") },
                      { id: "comments", label: t("Comments") },
                    ]}
                    activeTab={activeHeaderDetailTab}
                    onTabChange={setActiveHeaderDetailTab}
                    actionsMenuItems={[
                      {
                        label: t("All client characterizations"),
                        icon: (
                          <i
                            className={`fas fa-history ${iconGray}`}
                            aria-hidden
                          />
                        ),
                        onClick: () => {},
                      },
                      { label: "Pdf", icon: exportIconGray, onClick: () => {} },
                      { label: "Csv", icon: exportIconGray, onClick: () => {} },
                      {
                        label: "Envoyer par email",
                        icon: (
                          <i
                            className={`fas fa-envelope ${iconGray}`}
                            aria-hidden
                          />
                        ),
                        onClick: () => {},
                      },
                    ]}
                  />
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#222222] mb-3">
                    Monitoring — Liste
                  </h3>
                  <PageHeader
                    title={t("My Monitoring")}
                    tabs={[
                      { id: "zones", label: t("Active Zones") },
                      { id: "planning", label: t("Filling Planning") },
                      { id: "history", label: t("Historique") },
                    ]}
                    activeTab="zones"
                    onTabChange={() => {}}
                    shareButton={
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold text-[#222222] border border-[#d3d3d3] bg-white hover:bg-[#f8f8f8] transition-colors"
                      >
                        Lorem
                      </button>
                    }
                    actionsMenuItems={[
                      {
                        label: "Lorem",
                        icon: (
                          <img src={settingsSvg} alt="" className="w-4 h-4" />
                        ),
                        onClick: () => {},
                      },
                    ]}
                  />
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#222222] mb-3">
                    Monitoring — Détail
                  </h3>
                  <PageHeader
                    showBackButton
                    backRoute="#"
                    backLabel={t("Back to Monitoring")}
                    titleContent={
                      <div className="flex items-center gap-3 flex-wrap">
                        <span>Lorem ipsum flux</span>
                        <span
                          className="inline-flex items-center justify-center rounded-full text-sm font-bold text-[#2563EB]"
                          style={{
                            padding: "4px 12px",
                            height: "30px",
                            background: "#E6ECFD",
                            borderRadius: "1000px",
                          }}
                        >
                          20 m3
                        </span>
                        <span
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-white text-[#222222]"
                          style={{ border: "1.5px solid #030303" }}
                        >
                          Lorem ipsum address
                        </span>
                      </div>
                    }
                    titleContentMain={<span>Lorem ipsum flux</span>}
                    titleContentBadges={
                      <>
                        <span
                          className="inline-flex items-center justify-center rounded-full text-sm font-bold text-[#2563EB]"
                          style={{
                            padding: "4px 12px",
                            height: "30px",
                            background: "#E6ECFD",
                            borderRadius: "1000px",
                          }}
                        >
                          20 m3
                        </span>
                        <span
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-white text-[#222222]"
                          style={{ border: "1.5px solid #030303" }}
                        >
                          Lorem ipsum address
                        </span>
                      </>
                    }
                    subtitle="Lorem ipsum positioning — Date de dépôt : Lorem ipsum"
                    tabs={[
                      { id: "activity", label: t("Activity") },
                      { id: "filling", label: t("Filling level") },
                      { id: "quality", label: t("Classification/Quality") },
                      { id: "details", label: t("Characteristics") },
                    ]}
                    activeTab={activeMonitoringDetailTab}
                    onTabChange={setActiveMonitoringDetailTab}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {activeUiKitTab === "filtres" && <UiKitFiltersDemo />}

        {activeUiKitTab === "steppers" && (
          <section className="mb-8">
            <h2 className="char-section-header-title mb-4">{t("Steppers")}</h2>
            <p className="char-text-14 char-text-secondary mb-6">
              {t(
                "Stepper vertical partagé (composant Stepper, ui/components). Utilisé sur toutes les pages de création de la section Paramètres. Référence : « Nouveau Site ».",
              )}
            </p>
            <div className="border border-gray-200 rounded-lg bg-white p-6 max-w-md">
              <h3 className="text-lg font-semibold text-[#222222] mb-1">
                {t("Cliquable")} — {t("New site")}
              </h3>
              <p className="char-text-14 char-text-secondary mb-4">
                {t(
                  "Étapes atteignables cliquables (onStepClick). État courant, terminé (✓) et à venir.",
                )}
              </p>
              <Stepper
                steps={[
                  { key: "general", label: t("General information") },
                  { key: "hours", label: t("Opening hours") },
                  { key: "pro", label: t("Professional treatment") },
                  { key: "household", label: t("Household treatment") },
                ]}
                currentIndex={stepperDemoIndex}
                maxReachedIndex={3}
                onStepClick={(index) => setStepperDemoIndex(index)}
              />
            </div>
          </section>
        )}

        {activeUiKitTab === "couleur" && (
          <section className="mb-8">
            <h2 className="char-section-header-title mb-4">
              {t("Palette de couleur")}
            </h2>
            <p className="char-text-14 char-text-secondary mb-4">
              {t(
                "Couleurs de la plateforme (variables CSS dans themes/_colors.scss).",
              )}
            </p>
            <div className="grid grid-cols-2 min-1000:grid-cols-4 gap-4 w-full">
              {PALETTE_COLORS.map((item) => (
                <div
                  key={item.var}
                  className="flex flex-col border border-gray-200 rounded-lg bg-white overflow-hidden"
                >
                  <div
                    className="h-16 w-full shrink-0"
                    style={{ backgroundColor: item.hex }}
                  />
                  <div className="p-3">
                    <p className="char-text-14 char-text-primary font-medium m-0">
                      {item.name}
                    </p>
                    <p className="text-xs text-gray-500 m-0 mt-0.5 font-mono">
                      {item.var}
                    </p>
                    <p className="text-xs text-gray-500 m-0 mt-0.5 font-mono">
                      {item.hex}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {activeUiKitTab === "logo" && (
          <>
            <section className="mb-8">
              <h2 className="char-section-header-title mb-4">
                {t("Logos SVG")}
              </h2>
              <p className="char-text-14 char-text-secondary mb-4">
                {t("Logos SVG description")}
              </p>
              <div className="grid grid-cols-1 min-1000:grid-cols-3 gap-4 w-full">
                {SVG_LOGO_ITEMS.map((item) => (
                  <div
                    key={item.file}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white"
                  >
                    <img
                      src={item.src}
                      alt=""
                      className="shrink-0 object-contain"
                      style={{
                        width:
                          item.file === "Empty-folder.svg"
                            ? 44
                            : item.file.startsWith("icons/settings/")
                              ? 32
                              : 40,
                        height:
                          item.file === "Empty-folder.svg"
                            ? 44
                            : item.file.startsWith("icons/settings/")
                              ? 28
                              : 40,
                      }}
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <p className="char-text-14 char-text-primary font-medium m-0">
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-500 m-0 mt-0.5 font-mono">
                        {item.file}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-8">
              <h2 className="char-section-header-title mb-4">
                {t("Font Awesome")}
              </h2>
              <p className="char-text-14 char-text-secondary mb-4">
                {t(
                  "Icônes Font Awesome utilisées dans la plateforme. Classe : fas {icon} (ex. fas fa-search).",
                )}
              </p>
              <div className="grid grid-cols-2 min-1000:grid-cols-4 gap-4 w-full">
                {FONT_AWESOME_ICONS.map((item) => (
                  <div
                    key={item.class}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white"
                  >
                    <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded bg-gray-100 text-[#00A7A6]">
                      <i className={`fas ${item.class} text-lg`} aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <p className="char-text-14 char-text-primary font-medium m-0">
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-500 m-0 mt-0.5 font-mono">
                        fas {item.class}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default UiKit;
